import React, { useState } from "react";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { usePushSubscription } from "../hooks/usePushSubscription";
import ShareIcon from "./ShareIcon";
import InstallIcon from "./InstallIcon";
import LinkIcon from "./LinkIcon";
import { publicUrl } from "../utils/publicUrl";
import { t } from "../i18n";
import { T } from "../i18n/T";

interface Props {
  hideWhenInstalled?: boolean;
}

// True when the React app is running inside the Electron desktop
// companion (preload.js sets window.electronAPI.isElectron). In that
// mode the user already HAS the desktop app, so nudging them to "install
// on Safari" is nonsensical noise inside the overlay panel.
const isInsideElectronApp =
  typeof window !== "undefined" &&
  !!(window as any).electronAPI?.isElectron;

// Per-browser memory that this person already HAS the desktop app. Websites
// cannot detect installed apps (browser privacy), so we stamp the browser at
// the moments we KNOW: (1) the sign-in handoff window (?desktop=1) — that
// visit definitionally came FROM the app (LoginPage sets the flag); (2) a
// Download click (strong signal — set below). Founder request:
// "don't show the purple banner again once they have it."
export const HAS_DESKTOP_APP_KEY = "requestapp.hasDesktopApp";
export const markHasDesktopApp = () => {
  try {
    localStorage.setItem(HAS_DESKTOP_APP_KEY, "1");
  } catch {}
};
const hasDesktopApp = () => {
  try {
    return localStorage.getItem(HAS_DESKTOP_APP_KEY) === "1";
  } catch {
    return false;
  }
};

// Direct-download URL for the macOS desktop app. Hosted in the SEPARATE
// public `desktop-releases` repo because this (source) repo is private — a
// private-repo asset URL hits a GitHub login wall instead of downloading.
// Points at /releases/latest so every new release auto-resolves here with no
// code change — the published zip MUST stay named exactly "Request-App.zip".
export const MAC_DOWNLOAD_URL =
  "https://github.com/djrequest/desktop-releases/releases/latest/download/Request-App.zip";

const DesktopInstallBanner: React.FC<Props> = ({ hideWhenInstalled = false }) => {
  const { canInstall, installed, dismissed, isDesktop, isSafari, isMac, promptInstall, dismiss } =
    useInstallPrompt();
  const { enable: enablePush, busy: pushBusy, subscribed } = usePushSubscription({
    enabled: installed && !hideWhenInstalled,
  });
  const [stage, setStage] = useState<"install" | "permission" | "done" | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Suppress the "install on Safari" / "install in Chrome" prompts when
  // we're already INSIDE the native desktop companion — the DJ has the
  // app, telling them to install it again is confusing noise. Early-
  // returns live AFTER all hook calls to keep the Rules of Hooks happy.
  if (isInsideElectronApp) return null;
  // Browser already known to have the desktop app -> no install nudges at all.
  if (hasDesktopApp()) return null;

  const copyInstallLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl(""));
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      /* clipboard might be blocked — ignore */
    }
  };

  const closeBtn = (
    <button type="button" onClick={dismiss} style={closeButtonStyle} aria-label={t("desktopInstallBanner.dismiss")}>
      ×
    </button>
  );

  const proTipRow = isMac ? (
    <div style={secondaryRowStyle}>
      <div style={iconBoxStyle} aria-hidden>💡</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={proTipLabelStyle}>{t("desktopInstallBanner.proTip")}</span>{" "}
        <em style={proTipTextStyle}>{t("desktopInstallBanner.safariGivesYouA")}</em>
      </div>
      <button type="button" onClick={copyInstallLink} style={secondaryButtonStyle}>
        <LinkIcon />
        {linkCopied ? t("desktopInstallBanner.copied") : t("desktopInstallBanner.copyLinkForSafari")}
      </button>
    </div>
  ) : null;

  if (!isDesktop) return null;
  if (dismissed) return null;

  if (installed) {
    if (hideWhenInstalled) return null;
    if (subscribed) return null;
    if (stage === "done") return null;
    return (
      <div style={bannerStyle}>
        {closeBtn}
        <div style={mainRowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={titleStyle}>{t("desktopInstallBanner.youReInstalledEnable")}</div>
            <div style={subtitleStyle}>
              {t("desktopInstallBanner.turnOnDesktopNotifications")}
            </div>
          </div>
          <button
            style={primaryButtonStyle}
            disabled={pushBusy}
            onClick={async () => {
              await enablePush();
              setStage("done");
            }}
          >
            {pushBusy ? t("desktopInstallBanner.enabling") : t("desktopInstallBanner.enableNotifications")}
          </button>
        </div>
      </div>
    );
  }

  // Mac → push the native desktop companion instead of a PWA nudge. The
  // floating overlay is the headline Mac experience, so this branch sits
  // ABOVE the Safari "Add to Dock" / Chrome "Install" PWA branches below.
  if (isMac) {
    return (
      <div style={bannerStyle}>
        {closeBtn}
        <div style={mainRowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={titleStyle}>{t("desktopInstallBanner.getRequestAppFor")}</div>
            <div style={subtitleStyle}>
              {t("desktopInstallBanner.theDesktopAppFloats")}
            </div>
          </div>
          <a href={MAC_DOWNLOAD_URL} download style={downloadButtonStyle} onClick={markHasDesktopApp}>
            <InstallIcon size={18} />
            {t("desktopInstallBanner.downloadForMac")}
          </a>
        </div>
      </div>
    );
  }

  // Safari on Mac: no beforeinstallprompt event
  if (isSafari) {
    return (
      <div style={bannerStyle}>
        {closeBtn}
        <div style={mainRowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={titleStyle}>{t("desktopInstallBanner.getRequestAppOn")}</div>
            <div style={subtitleStyle}>
              {t("desktopInstallBanner.usersWhoSwitchTo")}
            </div>
          </div>
        </div>
        <div style={dividerStyle} />
        <div style={secondaryRowStyle}>
          <div style={iconBoxStyle} aria-hidden><ShareIcon size={20} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <T k="desktopInstallBanner.clickShareAddTo" tags={{ 0: (c) => <strong>{c}</strong>, 1: <ShareIcon />, 2: (c) => <strong>{c}</strong> }} />
          </div>
        </div>
      </div>
    );
  }

  // Chrome / Edge without the auto-install prompt — show manual instructions
  if (!canInstall) {
    return (
      <div style={bannerStyle}>
        {closeBtn}
        <div style={mainRowStyle}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={titleStyle}>{t("desktopInstallBanner.getRequestAppOn")}</div>
            <div style={subtitleStyle}>
              {t("desktopInstallBanner.usersWhoSwitchTo")}
            </div>
          </div>
        </div>
        <div style={dividerStyle} />
        <div style={secondaryRowStyle}>
          <div style={iconBoxStyle} aria-hidden><InstallIcon size={20} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <T k="desktopInstallBanner.clickTheInstallIcon" tags={{ 0: (c) => <strong>{c}</strong>, 1: <InstallIcon />, 2: (c) => <strong>{c}</strong>, 3: (c) => <strong>{c}</strong> }} />
          </div>
        </div>
        {proTipRow && <div style={dividerStyle} />}
        {proTipRow}
      </div>
    );
  }

  // Chrome / Edge with the native install prompt ready to fire
  return (
    <div style={bannerStyle}>
      {closeBtn}
      <div style={mainRowStyle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={titleStyle}>{t("desktopInstallBanner.getRequestAppOn")}</div>
          <div style={subtitleStyle}>
            {t("desktopInstallBanner.usersWhoSwitchTo")}
          </div>
        </div>
        <button
          style={primaryButtonStyle}
          onClick={async () => {
            const outcome = await promptInstall();
            if (outcome === "accepted") setStage("permission");
          }}
        >
          <InstallIcon size={18} />
          {t("desktopInstallBanner.install")}
        </button>
      </div>
      {proTipRow && <div style={dividerStyle} />}
      {proTipRow}
    </div>
  );
};

const bannerStyle: React.CSSProperties = {
  position: "relative",
  background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)",
  color: "white",
  padding: "18px 22px",
  paddingRight: 50,
  borderRadius: 16,
  margin: "12px 0 18px",
  boxShadow: "0 10px 30px rgba(124, 58, 237, 0.3)",
};

const mainRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
};

const secondaryRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexWrap: "wrap",
  fontSize: "0.9rem",
  lineHeight: 1.4,
};

const dividerStyle: React.CSSProperties = {
  height: 1,
  background: "rgba(255,255,255,0.18)",
  margin: "14px 0",
};

const closeButtonStyle: React.CSSProperties = {
  position: "absolute",
  top: 8,
  right: 10,
  background: "transparent",
  border: "none",
  color: "rgba(255,255,255,0.8)",
  fontSize: 22,
  lineHeight: 1,
  cursor: "pointer",
  padding: "4px 8px",
  borderRadius: 6,
  fontWeight: 400,
};

const titleStyle: React.CSSProperties = {
  fontWeight: 800,
  fontSize: "1.35rem",
  marginBottom: 4,
  letterSpacing: "-0.2px",
  lineHeight: 1.2,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "0.95rem",
  opacity: 0.88,
  lineHeight: 1.45,
};

const iconBoxStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: 10,
  background: "rgba(255,255,255,0.12)",
  flexShrink: 0,
  fontSize: 20,
};

const proTipLabelStyle: React.CSSProperties = {
  fontWeight: 700,
};

const proTipTextStyle: React.CSSProperties = {
  fontStyle: "italic",
  opacity: 0.95,
};

const primaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "white",
  color: "#5b21b6",
  border: "none",
  borderRadius: 10,
  padding: "12px 22px",
  fontWeight: 700,
  fontSize: "1rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxShadow: "0 4px 16px rgba(255,255,255,0.15)",
  flexShrink: 0,
};

// Anchor styled as the primary button (download is a plain link, not a button).
const downloadButtonStyle: React.CSSProperties = {
  ...primaryButtonStyle,
  textDecoration: "none",
};

const secondaryButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  background: "rgba(255,255,255,0.14)",
  color: "white",
  border: "1px solid rgba(255,255,255,0.28)",
  borderRadius: 8,
  padding: "7px 14px",
  fontSize: "0.82rem",
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

export default DesktopInstallBanner;
